import {
  CardComponent,
  CardCVV,
  CardExpiry,
  CardNumber,
} from "@chargebee/chargebee-js-react-wrapper";
import { valibotResolver } from "@hookform/resolvers/valibot";
import clsx from "clsx";
import { useCallback, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import {
  CreditCardSchema,
  type CreditCardSchemaType,
} from "features/payment-methods/schemas";
import type { PaymentSource } from "features/payment-methods/types";

import PoweredByStripe from "common/components/PoweredByStripe";
import { Button } from "common/components/ui/Button";
import ErrorMessage from "common/components/ui/ErrorMessage";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import { getWhitelabelCustomization } from "common/helpers/utils";
import useGTM from "common/hooks/useGTM";

import usePaymentSourcesMutations from "../../../datahooks/usePaymentSourcesMutations";
import CountrySelect from "./CountrySelect";

import type ChargebeeComponents from "@chargebee/chargebee-js-react-wrapper/dist/components/ComponentGroup";

interface CreditCardProps {
  onBack: () => void;
  onSuccess: (paymentSource: PaymentSource) => void;
}

const chargebeeInputClassName =
  "rounded-xl p-3 h-10 cursor-text bg-black-100 border text-sm-medium border-black-100 transition-[background,border]";

export default function CreditCard({ onSuccess, onBack }: CreditCardProps) {
  const cardRef = useRef<ChargebeeComponents>();

  const [isAuthorizing3ds, setIsAuthorizing3ds] = useState(false);
  const [chargebeeErrors, setChargebeeErrors] = useState<Record<string, Error>>(
    {},
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreditCardSchemaType>({
    resolver: valibotResolver(CreditCardSchema),
    defaultValues: {
      nameOnCard: "",
      cardBrand: "",
      country: "",
    },
  });

  const {
    getPaymentIntent,
    isGettingPaymentIntent,
    getPaymentIntentError,
    addPaymentSourceFromIntent,
    isAddingPaymentSourceFromIntent,
    addPaymentSourceFromIntentError,
    resetAddPaymentSourceFromIntentError,
  } = usePaymentSourcesMutations();

  const { sendEvent } = useGTM();

  const handleChange = useCallback(
    (state: ChargebeeCurrentState) => {
      const { cardType, error, field } = state;

      setChargebeeErrors((prev) => ({ ...prev, [field]: error }));

      if (field === "number" && cardType) {
        setValue("cardBrand", cardType, { shouldValidate: true });
      }
    },
    [setValue],
  );

  function onSubmit({ nameOnCard, cardBrand, country }: CreditCardSchemaType) {
    const hasChargebeeFieldErrors = Object.entries(chargebeeErrors).some(
      ([key, value]) => key !== "intent" && value,
    );

    if (!hasChargebeeFieldErrors) {
      //this resets the chargebee "intent" error
      setChargebeeErrors({});
      resetAddPaymentSourceFromIntentError();

      getPaymentIntent({
        brand: cardBrand,
        countryCode: country,
      }).then((intent) => {
        setIsAuthorizing3ds(true);
        cardRef.current
          .authorizeWith3ds(intent, { name: nameOnCard }, {})
          .then((authorizedPaymentIntent) => {
            addPaymentSourceFromIntent({
              intentId: authorizedPaymentIntent.id,
            })
              .then((paymentSource) => {
                onSuccess(paymentSource);
              })
              .catch(() => {
                sendEvent("add_payment_source_fail", ({ email }) => ({
                  email,
                  payment_method: "card",
                }));
              });
          })
          .catch((error) => {
            setChargebeeErrors({ intent: error });
          })
          .finally(() => {
            setIsAuthorizing3ds(false);
          });
      });
    }
  }

  const isBusy =
    isAuthorizing3ds ||
    isGettingPaymentIntent ||
    isAddingPaymentSourceFromIntent;

  return (
    <>
      <div className="flex flex-col gap-y-3">
        <FormField
          label="Card holder's name"
          htmlFor="cardHolder"
          error={errors.nameOnCard?.message}
        >
          <Input
            id="cardHolder"
            placeholder="Example: John Doe"
            {...register("nameOnCard")}
          />
        </FormField>

        <FormField
          label="Country"
          htmlFor="country"
          error={errors.country?.message}
        >
          <CountrySelect
            selectedCountry={watch("country")}
            onSelect={(value) =>
              setValue("country", value, { shouldValidate: true })
            }
          />
        </FormField>

        {/* Note: Known issue in Chargebee (Strict Mode extra iframe) -> https://github.com/chargebee/chargebee-js-wrappers/issues/98 */}
        <CardComponent
          ref={cardRef}
          classes={{
            invalid:
              "border-red-400 [&.focus]:outline-red-300 [&.focus]:outline-solid [&.focus]:outline-2",
            focus: clsx(
              "bg-white focus",
              getWhitelabelCustomization()?.primary_color
                ? "border-whitelabel-primary"
                : "border-purple-400",
            ),
          }}
          // @ts-expect-error: the chargebee type definitions are wrong
          onChange={handleChange}
        >
          <div className="flex flex-col gap-4">
            <FormField
              label="Card number"
              htmlFor="cardNumber"
              error={chargebeeErrors.number?.message}
            >
              <CardNumber
                className={chargebeeInputClassName}
                placeholder="Card number"
              />
            </FormField>

            <div className="flex justify-start gap-x-4">
              <FormField
                label="Expiry date"
                htmlFor="expiryDate"
                error={chargebeeErrors.expiry?.message}
                className="w-1/2"
              >
                <CardExpiry className={chargebeeInputClassName} />
              </FormField>

              <FormField
                label="CVC"
                htmlFor="cvc"
                error={chargebeeErrors.cvv?.message}
                className="w-1/2"
              >
                <CardCVV className={chargebeeInputClassName} />
              </FormField>
            </div>
          </div>
        </CardComponent>

        <PoweredByStripe />

        <ErrorMessage
          error={
            chargebeeErrors.intent ||
            getPaymentIntentError ||
            addPaymentSourceFromIntentError
          }
          fallbackErrorMessage="Error adding card"
          size="sm"
        />
      </div>
      <div className="mt-8 flex justify-between gap-x-2.5">
        <Button onClick={onBack} disabled={isBusy} variant="secondary-black">
          Back
        </Button>

        <Button
          onClick={() => {
            //we only use this to trigger chargebee field validation, since there is no other way to do it
            cardRef.current.tokenize({});
            handleSubmit(onSubmit)();
          }}
          isLoading={isBusy}
        >
          Add card
        </Button>
      </div>
    </>
  );
}
